import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NetworkProvider } from "./contexts/NetworkContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TabletProvider } from "./contexts/TabletContext";
import { EstablishmentProvider } from "./components/EstablishmentSelector";
import { SecurityNotificationsProvider } from "./contexts/SecurityNotificationsContext";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineBanner from "./components/OfflineBanner";
import SubdomainRedirect from "./components/SubdomainRedirect";
import { isCaisseSubdomain, isClientSubdomain } from "./utils/subdomainRouter";

// Pages
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Client Interface (Public)
import ClientLayout from "./client/components/ClientLayout";
import ClientHomePage from "./client/pages/ClientHomePage";
import ClientMenuPage from "./client/pages/ClientMenuPage";
import ClientCocktailsPage from "./client/pages/ClientCocktailsPage";
import ClientReservationPage from "./client/pages/ClientReservationPage";
import ClientContactPage from "./client/pages/ClientContactPage";
import ClientLoyaltyPage from "./client/pages/ClientLoyaltyPage";
import AboutPage from "./client/pages/AboutPage";
import TermsPage from "./client/pages/TermsPage";
import DashboardPage from "./pages/DashboardPage";
import ProductsPage from "./pages/ProductsPage";
import StockDashboardPage from "./pages/StockDashboardPage";
import CategoriesPage from "./pages/CategoriesPage";
import PurchasesPage from "./pages/PurchasesPage";
import ShoppingListsPageV2 from "./pages/ShoppingListsPageV2";
import PurchasesPageV3 from "./pages/PurchasesPageV3";
import BudgetManagementPage from "./pages/BudgetManagementPage";
import StockMovementsPage from "./pages/StockMovementsPage";
import ProfitabilityPage from "./pages/ProfitabilityPage";
import ReportsPage from "./pages/ReportsPage";
import AdminPage from "./pages/AdminPage";
import RecipesPage from "./pages/RecipesPage";
import MenuPage from "./pages/MenuPage";
import MenuProductsPage from "./pages/MenuProductsPage";
import DailyOutputsPage from "./pages/DailyOutputsPage";
import DailySalesPage from "./pages/DailySalesPage";
import DisbursementsPage from "./pages/DisbursementsPage";
import POSPageV2 from "./pages/POSPageV2";
import AuditLogPage from "./pages/AuditLogPage";
import ReorderPage from "./pages/ReorderPage";
import ExpensesPage from "./pages/ExpensesPage";
import ProfitLossPage from "./pages/ProfitLossPage";
// Personnel Module
import StaffPage from "./pages/StaffPage";
import PlanningPage from "./pages/PlanningPage";
import ClockPage from "./pages/ClockPage";
import AttendancePage from "./pages/AttendancePage";
import PayrollPage from "./pages/PayrollPage";
import TodayAttendancePage from "./pages/TodayAttendancePage";
import AlertsPage from "./pages/AlertsPage";
// Messaging Module
import MessagingPage from "./pages/MessagingPage";
import TeamChatPage from "./pages/TeamChatPage";
import MessagingProPage from "./pages/MessagingProPage";
import LivePresencePage from "./pages/LivePresencePage";
import ProfilePage from "./pages/ProfilePage";
// Cocktails Admin
import CocktailsAdminPage from "./pages/CocktailsAdminPage";
// Phase 3 - Reservations & Suppliers
import ReservationsPage from "./pages/ReservationsPage";
import SuppliersPage from "./pages/SuppliersPage";
// Phase 4 - AI Predictions
import AIPredictionsPage from "./pages/AIPredictionsPage";
import EmergencyHistoryPage from "./pages/EmergencyHistoryPage";
import StaffDetailPage from "./pages/StaffDetailPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import DirectorDashboardPage from "./pages/DirectorDashboardPage";
import DeputyDirectorDashboardPage from "./pages/DeputyDirectorDashboardPage";
import ServerDashboardPage from "./pages/ServerDashboardPage";
import CashierDashboardPage from "./pages/CashierDashboardPage";
import KitchenDashboardPage from "./pages/KitchenDashboardPage";
import BarDashboardPage from "./pages/BarDashboardPage";
import HotBeveragesDashboardPage from "./pages/HotBeveragesDashboardPage";
import EmployeePerformancePage from "./pages/EmployeePerformancePage";
import EstablishmentsPage from "./pages/EstablishmentsPage";
import TablesPage from "./pages/TablesPage";
// KitchenPage supprimé - doublon avec KitchenDashboardPage
import CashClosingPage from "./pages/CashClosingPage";
import CashClosingHistoryPage from "./pages/CashClosingHistoryPage";
import CashClosuresPage from "./pages/CashClosuresPage";
import CashSupervisionPage from "./pages/CashSupervisionPage";
import RegisterManagementPage from "./pages/RegisterManagementPage";
import LiveMonitoringPage from "./pages/LiveMonitoringPage";
import OvertimePage from "./pages/OvertimePage";
import FormulasAdminPage from "./pages/FormulasAdminPage";
import LoyaltyAdminPage from "./pages/LoyaltyAdminPage";
import SystemCredentialsPage from "./pages/SystemCredentialsPage";
import PermissionsManagementPage from "./pages/PermissionsManagementPage";
import RolesPermissionsPage from "./pages/RolesPermissionsPage";
import TableQRCodesPage from "./pages/TableQRCodesPage";
import SupervisionSettingsPage from "./pages/SupervisionSettingsPage";
import GuidePage from "./pages/GuidePage";
import ClientAdminPage from "./pages/ClientAdminPage";
import PendingClosuresPage from "./pages/PendingClosuresPage";
import FloorPlanPage from "./pages/FloorPlanPage";
import MotivationAdminPage from "./pages/MotivationAdminPage";
import ServerPerformancePage from "./pages/ServerPerformancePage";
import PerformancePage from "./pages/PerformancePage";
import AutoClosureAdminPage from "./pages/AutoClosureAdminPage";
import DailyReportsPage from "./pages/DailyReportsPage";
import ReservationActionPage from "./pages/ReservationActionPage";
import CategoryRoutingPage from "./pages/CategoryRoutingPage";
import AdminResetPage from "./pages/AdminResetPage";
import ForecastPage from "./pages/ForecastPage";
import SecurityPage from "./pages/SecurityPage";
import TreasuryPage from "./pages/TreasuryPage";
import Layout from "./components/Layout";
import AutoClosureNotification from "./components/AutoClosureNotification";
import NewDayBanner from "./components/NewDayBanner";
import ShiftReminderHandler from "./components/ShiftReminderHandler";

// Protected Route Component with role-based access
const ProtectedRoute = ({ children, adminOnly = false, requiredPermission = null }) => {
    const { isAuthenticated, loading, isDirecteur, hasPermission, user } = useAuth();
    const location = useLocation();
    const isServeur = user?.role === 'SERVEUR';
    const defaultRedirect = '/dashboard'; // Tous les utilisateurs vont au dashboard

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // Sauvegarder l'URL actuelle dans sessionStorage pour y revenir après login
        const currentPath = location.pathname + location.search;
        if (currentPath !== '/login' && currentPath !== '/') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
        }
        return <Navigate to="/login" state={{ from: currentPath }} replace />;
    }

    if (adminOnly && !isDirecteur) {
        return <Navigate to={defaultRedirect} replace />;
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
        return <Navigate to={defaultRedirect} replace />;
    }

    return children;
};

// Composant pour gérer la redirection après login
const AuthenticatedRedirect = () => {
    // Utiliser useMemo pour ne lire qu'une seule fois du sessionStorage
    const redirectPath = useMemo(() => {
        const path = sessionStorage.getItem('redirectAfterLogin') || '/dashboard';
        sessionStorage.removeItem('redirectAfterLogin');
        return path;
    }, []);
    
    return <Navigate to={redirectPath} replace />;
};

function AppRoutes() {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    // Redirection par défaut basée sur le sous-domaine
    // - caisse.aldrins-resto.com → /dashboard (après login)
    // - www.aldrins-resto.com → /client (page publique)
    const defaultRedirect = isCaisseSubdomain() ? '/dashboard' : '/client';

    return (
        <Routes>
            {/* ============ CLIENT PUBLIC INTERFACE ============ */}
            <Route path="/client" element={<ClientLayout />}>
                <Route index element={<ClientHomePage />} />
                <Route path="menu" element={<ClientMenuPage />} />
                <Route path="cocktails" element={<ClientCocktailsPage />} />
                <Route path="reservation" element={<ClientReservationPage />} />
                <Route path="contact" element={<ClientContactPage />} />
                <Route path="fidelite" element={<ClientLoyaltyPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="terms" element={<TermsPage />} />
            </Route>
            
            {/* Route directe pour la fidélité (QR code) */}
            <Route path="/fidelite" element={<ClientLoyaltyPage />} />
            
            {/* Route publique pour action de réservation (confirmation/annulation via email) */}
            <Route path="/reservation-action" element={<ReservationActionPage />} />
            
            {/* ============ INTERNAL RESTAURANT INTERFACE ============ */}
            <Route 
                path="/login" 
                element={isAuthenticated ? <AuthenticatedRedirect /> : <LoginPage />} 
            />
            <Route 
                path="/reset-password" 
                element={isAuthenticated ? <AuthenticatedRedirect /> : <ResetPasswordPage />} 
            />
            
            {/* Route racine: redirige selon le sous-domaine */}
            <Route 
                path="/" 
                element={<RootRedirect />} 
            />
            
            {/* Routes protégées (interface interne) */}
            <Route
                path="/app"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/dashboard" replace />} />
            </Route>
            
            <Route
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route path="dashboard" element={<DashboardPage />} />
                <Route 
                    path="motivation-admin" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <MotivationAdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin-dashboard" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <AdminDashboardPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="client-admin" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <ClientAdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="cocktails-admin" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <CocktailsAdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route path="director-dashboard" element={<DirectorDashboardPage />} />
                <Route path="deputy-director-dashboard" element={<DeputyDirectorDashboardPage />} />
                <Route path="server-dashboard" element={<ServerDashboardPage />} />
                <Route path="cashier-dashboard" element={<CashierDashboardPage />} />
                <Route path="kitchen-dashboard" element={<KitchenDashboardPage />} />
                <Route path="bar-dashboard" element={<BarDashboardPage />} />
                <Route path="hot-beverages" element={<HotBeveragesDashboardPage />} />
                <Route 
                    path="category-routing" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <CategoryRoutingPage />
                        </ProtectedRoute>
                    } 
                />
                <Route path="products" element={<ProductsPage />} />
                <Route path="stock-dashboard" element={<StockDashboardPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="purchases" element={<PurchasesPage />} />
                <Route path="shopping-lists" element={<ShoppingListsPageV2 />} />
                <Route path="achats" element={<PurchasesPageV3 />} />
                <Route path="budget" element={<BudgetManagementPage />} />
                <Route path="forecast" element={<ForecastPage />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="treasury" element={<TreasuryPage />} />
                <Route path="stock-movements" element={<StockMovementsPage />} />
                <Route path="profitability" element={<ProfitabilityPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="server-performance" element={<ServerPerformancePage />} />
                <Route path="recipes" element={<RecipesPage />} />
                <Route path="menu" element={<MenuPage />} />
                <Route path="menu-products" element={<MenuProductsPage />} />
                <Route path="daily-outputs" element={<DailyOutputsPage />} />
                <Route path="disbursements" element={<DisbursementsPage />} />
                <Route path="pos" element={<POSPageV2 />} />
                <Route path="pos-v2" element={<POSPageV2 />} />
                {/* Redirection de l'ancienne route pos-legacy vers la nouvelle */}
                <Route path="pos-legacy" element={<Navigate to="/pos-v2" replace />} />
                <Route 
                    path="supervision-settings" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <SupervisionSettingsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="cash-supervision" 
                    element={
                        <ProtectedRoute requiredPermission="view_cash_reports">
                            <CashSupervisionPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="register-management" 
                    element={
                        <ProtectedRoute requiredPermission="view_cash_reports">
                            <RegisterManagementPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="pending-closures" 
                    element={
                        <ProtectedRoute requiredPermission="view_cash_reports">
                            <PendingClosuresPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="reorder" 
                    element={
                        <ProtectedRoute requiredPermission="view_stock_forecasting">
                            <ReorderPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="expenses" 
                    element={
                        <ProtectedRoute requiredPermission="view_expenses">
                            <ExpensesPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="profit-loss" 
                    element={
                        <ProtectedRoute requiredPermission="view_profit_loss">
                            <ProfitLossPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="daily-sales" 
                    element={
                        <ProtectedRoute requiredPermission="view_daily_sales">
                            <DailySalesPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Personnel routes */}
                <Route path="clock" element={<ClockPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route 
                    path="employee-performance" 
                    element={
                        <ProtectedRoute>
                            <EmployeePerformancePage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="establishments" 
                    element={
                        <ProtectedRoute>
                            <EstablishmentsPage />
                        </ProtectedRoute>
                    } 
                />
                {/* /tables et /mes-tables → Vue grille des tables */}
                <Route 
                    path="tables" 
                    element={
                        <ProtectedRoute>
                            <TablesPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="mes-tables" 
                    element={
                        <ProtectedRoute>
                            <TablesPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="floor-plan" 
                    element={
                        <ProtectedRoute>
                            <FloorPlanPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="kitchen" 
                    element={
                        <Navigate to="/kitchen-dashboard" replace />
                    } 
                />
                <Route 
                    path="cash-closing" 
                    element={
                        <ProtectedRoute requiredPermission="close_cash_register">
                            <CashClosingPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="cash-closures" 
                    element={
                        <ProtectedRoute>
                            <CashClosuresPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="cash-closing-history" 
                    element={
                        <ProtectedRoute>
                            <CashClosingHistoryPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="live-monitoring" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <LiveMonitoringPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="performance" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <PerformancePage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="staff" 
                    element={
                        <ProtectedRoute requiredPermission="view_staff">
                            <StaffPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="staff/:id" 
                    element={
                        <ProtectedRoute requiredPermission="view_staff">
                            <StaffDetailPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="planning" 
                    element={
                        <ProtectedRoute requiredPermission="view_planning">
                            <PlanningPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="overtime" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <OvertimePage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="formulas" 
                    element={
                        <ProtectedRoute>
                            <FormulasAdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="loyalty" 
                    element={
                        <ProtectedRoute>
                            <LoyaltyAdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="table-qrcodes" 
                    element={
                        <ProtectedRoute>
                            <TableQRCodesPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="attendance" 
                    element={
                        <ProtectedRoute requiredPermission="view_attendance">
                            <AttendancePage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="today-attendance" 
                    element={
                        <ProtectedRoute requiredPermission="view_attendance">
                            <TodayAttendancePage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="alerts" 
                    element={
                        <ProtectedRoute requiredPermission="view_attendance">
                            <AlertsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="payroll" 
                    element={
                        <ProtectedRoute requiredPermission="view_payroll">
                            <PayrollPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Messaging route */}
                <Route 
                    path="messaging" 
                    element={
                        <ProtectedRoute requiredPermission="view_messaging">
                            <MessagingPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Team Chat - Unified Messaging */}
                <Route 
                    path="team-chat" 
                    element={
                        <ProtectedRoute>
                            <TeamChatPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Messaging Pro - Nouvelle Messagerie Professionnelle */}
                <Route 
                    path="messagerie-pro" 
                    element={
                        <ProtectedRoute>
                            <MessagingProPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Live Presence (Direction only) */}
                <Route 
                    path="live-presence" 
                    element={
                        <ProtectedRoute requiredPermission="view_live_presence">
                            <LivePresencePage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Emergency History (ADMIN & DIRECTEUR only) */}
                <Route 
                    path="emergency-history" 
                    element={
                        <ProtectedRoute requiredPermission="view_emergency_history">
                            <EmergencyHistoryPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Phase 3 - Reservations & Suppliers */}
                <Route 
                    path="reservations" 
                    element={
                        <ProtectedRoute>
                            <ReservationsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="suppliers" 
                    element={
                        <ProtectedRoute>
                            <SuppliersPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Phase 4 - AI Predictions */}
                <Route 
                    path="ai-predictions" 
                    element={
                        <ProtectedRoute>
                            <AIPredictionsPage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Guide interactif */}
                <Route 
                    path="guide" 
                    element={
                        <ProtectedRoute>
                            <GuidePage />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Admin routes */}
                <Route 
                    path="admin" 
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin/audit" 
                    element={
                        <ProtectedRoute adminOnly>
                            <AuditLogPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin/settings" 
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin/credentials" 
                    element={
                        <ProtectedRoute adminOnly>
                            <SystemCredentialsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin/permissions" 
                    element={
                        <ProtectedRoute adminOnly>
                            <PermissionsManagementPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin/roles-permissions" 
                    element={
                        <ProtectedRoute adminOnly>
                            <RolesPermissionsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin/auto-closure" 
                    element={
                        <ProtectedRoute adminOnly>
                            <AutoClosureAdminPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="daily-reports" 
                    element={
                        <ProtectedRoute>
                            <DailyReportsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="admin-reset" 
                    element={
                        <ProtectedRoute adminOnly={true}>
                            <AdminResetPage />
                        </ProtectedRoute>
                    } 
                />
            </Route>
            {/* Catch-all: redirige selon le sous-domaine */}
            <Route path="*" element={<SubdomainCatchAll />} />
        </Routes>
    );
}

// Composant pour gérer la redirection catch-all basée sur le sous-domaine
function SubdomainCatchAll() {
    const { isAuthenticated } = useAuth();
    
    // Si sur sous-domaine caisse et authentifié → dashboard
    if (isCaisseSubdomain() && isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    // Si sur sous-domaine caisse et non authentifié → login
    if (isCaisseSubdomain() && !isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    // Sinon (domaine client) → page client
    return <Navigate to="/client" replace />;
}

// Composant pour gérer la redirection de la route racine "/"
function RootRedirect() {
    const { isAuthenticated } = useAuth();
    
    // Si sur sous-domaine caisse
    if (isCaisseSubdomain()) {
        if (isAuthenticated) {
            return <Navigate to="/dashboard" replace />;
        }
        return <Navigate to="/login" replace />;
    }
    
    // Si authentifié sur domaine client, aller au dashboard
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    // Sinon, aller à la page client publique
    return <Navigate to="/client" replace />;
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <TabletProvider>
                    <AuthProvider>
                        <SecurityNotificationsProvider>
                            <EstablishmentProvider>
                                <NetworkProvider>
                                    <SubdomainRedirect />
                                    <NewDayBanner />
                                    <OfflineBanner />
                                    <AppRoutes />
                                    <Toaster position="top-right" richColors />
                                    <PWAInstallPrompt />
                                    <AutoClosureNotification />
                                    <ShiftReminderHandler />
                                </NetworkProvider>
                            </EstablishmentProvider>
                        </SecurityNotificationsProvider>
                    </AuthProvider>
                </TabletProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
